import { useState, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, mangaList } from '@/mocks/mangaData';

function StarRating({ rating }: { rating: number }) {
  const full = Math.floor(rating);
  const half = rating - full >= 0.5;
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }, (_, i) => (
        <i
          key={i}
          className={`text-xs ${
            i < full
              ? 'ri-star-fill text-accent-500'
              : i === full && half
              ? 'ri-star-half-fill text-accent-500'
              : 'ri-star-fill text-background-300'
          }`}
        ></i>
      ))}
      <span className="ml-1 text-xs font-semibold text-foreground-700">{rating}</span>
    </div>
  );
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');
  const [mobileCatOpen, setMobileCatOpen] = useState(false);

  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];

  const filteredManga = useMemo(() => {
    let list = mangaList.filter((m) => m.category === currentCategory.id);

    if (sortBy === 'latest') {
      list = [...list].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    } else if (sortBy === 'popular') {
      list = [...list].sort((a, b) => b.views - a.views);
    } else if (sortBy === 'rating') {
      list = [...list].sort((a, b) => b.rating - a.rating);
    }

    return list;
  }, [currentCategory, sortBy]);

  // If slug not found, redirect
  if (!categories.find((c) => c.slug === slug)) {
    navigate('/category/shaonian', { replace: true });
    return null;
  }

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - 36漫画网`,
    description: `36漫画网${currentCategory.name}分类，为您提供最全最新的${currentCategory.name}漫画，在线免费阅读全集无删减。`,
    url: `https://36manhua.com/category/${currentCategory.slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '36漫画网',
      url: 'https://36manhua.com',
    },
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(pageSchema) }}
      />

      <Navbar />

      <main className="pt-20">
        <div className="px-4 md:px-6 max-w-7xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: currentCategory.name },
            ]}
          />

          <div className="mt-6 mb-8">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
              {currentCategory.name}
            </h1>
            <p className="mt-2 text-sm text-foreground-500">
              共 {filteredManga.length} 部漫画 · 在线免费阅读全集无删减
            </p>
          </div>

          {/* Mobile category dropdown */}
          <div className="lg:hidden mb-6">
            <button
              onClick={() => setMobileCatOpen(!mobileCatOpen)}
              className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-background-100 text-sm font-medium text-foreground-700"
            >
              <span className="flex items-center gap-2">
                <i className="ri-menu-line"></i> {currentCategory.name}
              </span>
              {mobileCatOpen ? (
                <i className="ri-arrow-up-s-line"></i>
              ) : (
                <i className="ri-arrow-down-s-line"></i>
              )}
            </button>
            {mobileCatOpen && (
              <div className="mt-2 p-2 rounded-xl bg-background-100 grid grid-cols-2 gap-1">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    onClick={() => setMobileCatOpen(false)}
                    className={`px-3 py-2 rounded-lg text-sm transition-colors whitespace-nowrap ${
                      cat.slug === slug
                        ? 'bg-primary-100 text-primary-700 font-medium'
                        : 'text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar categories */}
            <aside className="hidden lg:block w-48 shrink-0">
              <div className="sticky top-24">
                <h3 className="text-sm font-semibold text-foreground-900 mb-3">漫画分类</h3>
                <nav className="flex flex-col gap-1">
                  {categories.map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/category/${cat.slug}`}
                      className={`px-3 py-2.5 rounded-lg text-sm transition-colors whitespace-nowrap ${
                        cat.slug === slug
                          ? 'bg-primary-100 text-primary-700 font-medium'
                          : 'text-foreground-600 hover:bg-background-100'
                      }`}
                    >
                      {cat.name}
                    </Link>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Content */}
            <div className="flex-1 min-w-0">
              {/* Sort */}
              <div className="flex items-center gap-2 mb-6">
                <span className="text-sm text-foreground-500 mr-2">排序：</span>
                {([
                  { key: 'latest', label: '最新' },
                  { key: 'popular', label: '最热' },
                  { key: 'rating', label: '评分' },
                ] as const).map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => setSortBy(opt.key)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                      sortBy === opt.key
                        ? 'bg-primary-100 text-primary-700'
                        : 'text-foreground-600 hover:bg-background-100'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              {/* Manga grid */}
              {filteredManga.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {filteredManga.map((manga) => (
                    <Link
                      key={manga.id}
                      to={`/article/${manga.id}-ch${manga.chapterCount}`}
                      className="group block"
                    >
                      <div className="relative overflow-hidden rounded-lg bg-background-100 aspect-[3/4]">
                        <img
                          src={manga.cover}
                          alt={manga.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          title={manga.title}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                        <div className="absolute bottom-0 left-0 right-0 p-3">
                          <span className="inline-block px-2 py-0.5 rounded-md bg-primary-500 text-white text-xs font-medium mb-1">
                            {manga.status}
                          </span>
                          <h3 className="text-sm font-semibold text-white line-clamp-1 font-heading">
                            {manga.title}
                          </h3>
                        </div>
                        <div className="absolute top-2 right-2">
                          <div className="px-2 py-0.5 rounded-md bg-black/60 backdrop-blur-sm text-white text-xs font-medium">
                            {manga.chapterCount}话
                          </div>
                        </div>
                      </div>
                      <div className="mt-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-foreground-500">{manga.author}</span>
                          <StarRating rating={manga.rating} />
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-book-2-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500">该分类暂无漫画</p>
                </div>
              )}

              {/* Pagination */}
              <div className="flex items-center justify-center gap-2 mt-10">
                <button className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center text-foreground-500 hover:bg-background-200 transition-colors cursor-pointer">
                  <i className="ri-arrow-left-s-line"></i>
                </button>
                <button className="w-9 h-9 rounded-lg bg-primary-500 text-white text-sm font-medium cursor-pointer">1</button>
                <button className="w-9 h-9 rounded-lg bg-background-100 text-foreground-600 text-sm hover:bg-background-200 transition-colors cursor-pointer">2</button>
                <button className="w-9 h-9 rounded-lg bg-background-100 text-foreground-600 text-sm hover:bg-background-200 transition-colors cursor-pointer">3</button>
                <button className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center text-foreground-500 hover:bg-background-200 transition-colors cursor-pointer">
                  <i className="ri-arrow-right-s-line"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <section className="mt-16 py-14 bg-background-950">
          <div className="px-4 md:px-6 max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-2xl font-bold text-white">
              找不到想看的漫画？
            </h2>
            <p className="mt-3 text-sm text-background-400">
              告诉我们你想看的漫画，我们会尽快为你收录！
            </p>
            <Link
              to="/about#contact"
              className="inline-flex items-center gap-2 mt-6 px-8 py-3 rounded-lg bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              联系我们 <i className="ri-mail-line"></i>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}